'use client'

import React, { useEffect, useState, useRef, useCallback } from 'react'
import type { FC } from 'react'
import { FullPost } from './full-post'
import { usePathname, useRouter } from 'next/navigation'

import type { Post } from '@payload-types'

interface InfiniteNewsProps {
  initialPost: Post // Only one initial post
  initialRandomPosts?: Post[]
  categoryId?: string
}

export const InfiniteNews: FC<InfiniteNewsProps> = ({
  initialPost,
  initialRandomPosts = [],
  categoryId,
}) => {
  const [posts, setPosts] = useState<Post[]>([initialPost])
  const [randomPosts, setRandomPosts] = useState<Post[]>(initialRandomPosts)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)

  // Separate ref for the end-of-article marker
  const endOfArticleRef = useRef<HTMLDivElement>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)

  const pathname = usePathname()
  const router = useRouter()

  // Keep track of posts we've already seen by ID to prevent duplicates
  const [seenPostIds, setSeenPostIds] = useState<Set<string>>(new Set([initialPost.id]))

  // Update URL when post changes (without reloading the page)
  useEffect(() => {
    // Skip for the initial post since its URL is already set
    if (posts.length > 1) {
      const currentPost = posts[posts.length - 1]
      // Only update if we have the necessary data
      if (currentPost && currentPost.slug && currentPost.displayId) {
        const newUrl = `/${currentPost.slug}/${currentPost.displayId}`
        // Use pushState to update URL without page reload
        window.history.pushState(null, '', newUrl)

        // Update page title if possible
        if (currentPost.title) {
          document.title = `${currentPost.title} | News Site`
        }
      }
    }
  }, [posts.length])

  const loadMorePosts = useCallback(async () => {
    if (loading || !hasMore) return

    try {
      setLoading(true)
      const nextPage = page + 1

      // Convert the Set of seen post IDs to a comma-separated string
      const seenIdsString = Array.from(seenPostIds).join(',')

      const queryParams = new URLSearchParams({
        page: nextPage.toString(),
        excludeIds: seenIdsString, // Pass the IDs we've already seen
        ...(categoryId ? { category: categoryId } : {}),
      })

      const res = await fetch(`/api/posts/infinite?${queryParams.toString()}`)
      const data = await res.json()

      if (!data.docs || data.docs.length === 0) {
        setHasMore(false)
      } else {
        // Ensure we're only adding one post
        const newPost = data.docs[0]

        // Double-check we haven't seen this post before (redundant but safe)
        if (newPost && newPost.id && !seenPostIds.has(newPost.id)) {
          setPosts((prev) => [...prev, newPost])
          // Add this post ID to our seen set
          setSeenPostIds((prev) => new Set(prev).add(newPost.id))

          // Update random posts if provided
          if (data.randomPosts && data.randomPosts.length > 0) {
            setRandomPosts(data.randomPosts)
          }
          setPage(nextPage)
        } else {
          // If we got a duplicate post despite our exclusion, skip it
          console.warn('Received duplicate post despite exclusion:', newPost?.id)
          setPage(nextPage)
          // Try again with a short delay if we still have more pages
          setTimeout(() => {
            loadMorePosts()
          }, 300)
        }
      }
    } catch (error) {
      console.error('Error fetching more posts:', error)
    } finally {
      setLoading(false)
    }
  }, [loading, hasMore, page, categoryId, seenPostIds])

  // Set up the intersection observer
  useEffect(() => {
    // Use a lower threshold to ensure user has scrolled deep into the content
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) {
          loadMorePosts()
        }
      },
      {
        // Require more visibility before triggering - 30% of the marker must be visible
        threshold: 0.3,
        // Add root margin to delay loading until user is well into the marker area
        rootMargin: '100px 0px',
      },
    )

    observerRef.current = observer

    return () => {
      observer.disconnect()
    }
  }, [loadMorePosts, hasMore, loading])

  // Observe the end-of-article marker
  useEffect(() => {
    const currentObserver = observerRef.current
    const currentEndOfArticleRef = endOfArticleRef.current

    if (currentEndOfArticleRef && currentObserver) {
      currentObserver.observe(currentEndOfArticleRef)
    }

    return () => {
      if (currentEndOfArticleRef && currentObserver) {
        currentObserver.unobserve(currentEndOfArticleRef)
      }
    }
  }, [posts])

  return (
    <div className="flex flex-col">
      {posts.map((post, index) => {
        const isLastPost = index === posts.length - 1

        return (
          <div key={`${post.id}-${index}`}>
            <FullPost
              post={post}
              randomPosts={randomPosts}
              isLast={isLastPost}
              scrollRef={isLastPost ? endOfArticleRef : undefined}
            />
          </div>
        )
      })}
      {loading && (
        <div className="flex justify-center py-4 mb-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
    </div>
  )
}
